import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MonogramSmall } from "@/components/Monogram";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F2ED] text-[#4A3E39]">
        <span className="eyebrow">Checking session…</span>
      </div>
    );
  }
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email.trim(), password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F2ED] flex flex-col" data-testid="admin-login-page">
      <header className="px-6 lg:px-14 py-8 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3">
          <MonogramSmall size={26} />
          <span className="font-display text-xl tracking-[0.18em]">T&amp;S IVORY</span>
        </a>
        <span className="eyebrow text-[#B7AB9B]">Admin Portal</span>
      </header>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="eyebrow mb-6 text-[#4A3E39]">Sign In</div>
          <h1 className="font-display font-light text-5xl md:text-6xl text-[#2C2724] leading-[1.05] mb-12">
            The Ivory<br /><em className="italic">control room</em>.
          </h1>

          <form onSubmit={submit} className="space-y-8" data-testid="admin-login-form">
            <div>
              <label htmlFor="email" className="eyebrow text-[#4A3E39]">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-email"
                required
                className="editorial-input mt-3"
                placeholder="admin@tsivory.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="eyebrow text-[#4A3E39]">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-password"
                required
                className="editorial-input mt-3"
                placeholder="•••••••••"
              />
            </div>

            {error && (
              <p data-testid="admin-login-error" className="text-sm text-[#6D1F2B]">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="btn-burgundy w-full justify-center disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <p className="mt-10 text-[12px] text-[#B7AB9B] leading-relaxed">
            Authorised personnel only. Activity is logged.
          </p>
        </div>
      </div>

      <footer className="px-6 lg:px-14 py-6 text-xs text-[#B7AB9B] flex justify-between">
        <a href="/" className="hover:text-[#2C2724]" data-testid="admin-back-home">← Back to site</a>
        <span>© {new Date().getFullYear()} T&amp;S Ivory</span>
      </footer>
    </div>
  );
}
